import React, { useState } from 'react';
import { Upload, X, Image, FileText, AlertCircle } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { getImageUrlWithFallback } from '../../utils/imageUtils';

const FileUpload = ({ 
  category, // 'products', 'ingredients', 'bottles'
  itemId, 
  currentImage, 
  onImageUploaded, 
  className = "" 
}) => {
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const { toast } = useToast();
  const { adminToken } = useAdminAuth();

  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'application/pdf'];
  const maxSize = 10 * 1024 * 1024; // 10MB

  const validateFile = (file) => {
    if (!allowedTypes.includes(file.type)) {
      throw new Error('Formato non supportato. Usa JPG, PNG, WebP o PDF');
    }
    
    if (file.size > maxSize) {
      throw new Error('File troppo grande. Massimo 10MB');
    }
  };

  const uploadFile = async (file) => {
    setUploading(true);
    
    try {
      validateFile(file);
      
      const formData = new FormData();
      formData.append('file', file);
      
      // Get backend URL from environment  
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      
      if (!adminToken) {
        throw new Error('Token admin non disponibile');
      }
      
      const response = await fetch(`${backendUrl}/api/upload/admin/${category}/${itemId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminToken}`
        },
        body: formData
      });
      
      const result = await response.json();
      
      if (response.ok && result.success) {
        console.log('✅ Upload successful!');
        console.log('📁 File path:', result.filePath);
        console.log('🔗 Full URL:', `${backendUrl}/api/upload/serve/${result.filePath}`);
        
        toast({
          title: "Upload completato",
          description: `${file.name} caricato con successo`,
          variant: "default"
        });
        
        if (onImageUploaded) {
          onImageUploaded(result.filePath);
        }
      } else {
        throw new Error(result.message || 'Errore durante l\'upload');
      }
      
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: "Errore upload",
        description: error.message || 'Errore durante l\'upload del file',
        variant: "destructive"
      });
    } finally {
      setUploading(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      uploadFile(file);
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragActive(false);
    
    const file = event.dataTransfer.files[0];
    if (file) {
      uploadFile(file);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    setDragActive(false);
  };

  const removeImage = () => {
    if (onImageUploaded) {
      onImageUploaded(null);
    }
  };

  const getFileIcon = (filePath) => {
    if (!filePath) return <Upload className="w-8 h-8 text-gray-400" />;
    
    const extension = filePath.split('.').pop()?.toLowerCase();
    if (extension === 'pdf') {
      return <FileText className="w-8 h-8 text-red-500" />;
    } else {
      return <Image className="w-8 h-8 text-blue-500" />;
    }
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Current Image Display */}
      {currentImage && (
        <div className="relative mb-4">
          <div className="bg-white p-4 rounded-lg border-2 border-green-200 bg-green-50">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center space-x-3">
                {getFileIcon(currentImage)}
                <div>
                  <p className="text-sm font-medium text-green-800">✅ File caricato con successo</p>
                  <p className="text-xs text-green-600">{currentImage.split('/').pop()}</p>
                </div>
              </div>
              <button
                onClick={removeImage}
                className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                title="Rimuovi file"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            
            {/* Image Preview (only for images) */}
            {currentImage && !currentImage.endsWith('.pdf') && (
              <div className="mt-3">
                <div className="relative">
                  <img 
                    src={(() => {
                      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
                      let servePath = currentImage;
                      
                      // Remove /uploads/ prefix if present
                      if (currentImage.startsWith('/uploads/')) {
                        servePath = currentImage.replace('/uploads/', '');
                      } else if (currentImage.startsWith('uploads/')) {
                        servePath = currentImage.replace('uploads/', '');
                      }
                      
                      const imageUrl = `${backendUrl}/api/upload/serve/${servePath}?t=${Date.now()}`;
                      console.log('🖼️ Displaying image:', imageUrl);
                      return imageUrl;
                    })()}
                    alt="Anteprima immagine"
                    className="w-full max-h-64 object-contain rounded-lg border-2 border-gray-200 bg-white shadow-sm"
                    key={currentImage} // Force re-render when image changes
                    onError={(e) => {
                      console.error('❌ Errore caricamento immagine:', currentImage);
                      console.error('❌ Failed URL:', e.target.src);
                      // Show placeholder on error
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div style={{display: 'none'}} className="flex items-center justify-center h-64 bg-gray-100 rounded-lg border-2 border-gray-200">
                    <div className="text-center">
                      <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-600">Impossibile caricare l'immagine</p>
                      <p className="text-xs text-gray-400 mt-1">{currentImage}</p>
                    </div>
                  </div>
                  <div className="absolute top-2 right-2 bg-white bg-opacity-90 px-2 py-1 rounded text-xs text-gray-600">
                    Anteprima
                  </div>
                </div>
              </div>
            )}
            
            {/* PDF Preview */}
            {currentImage && currentImage.endsWith('.pdf') && (
              <div className="mt-3 p-4 bg-red-50 border-2 border-dashed border-red-200 rounded-lg text-center">
                <FileText className="w-12 h-12 text-red-500 mx-auto mb-2" />
                <p className="text-sm text-red-700 font-medium">PDF caricato</p>
                <p className="text-xs text-red-600">Anteprima non disponibile per file PDF</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Upload Area */}
      <div 
        className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
          dragActive 
            ? 'border-purple-400 bg-purple-50' 
            : currentImage 
              ? 'border-gray-200 bg-gray-50' 
              : 'border-gray-300 hover:border-gray-400 bg-white'
        } ${uploading ? 'opacity-50 pointer-events-none' : ''} ${currentImage ? 'opacity-60' : ''}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        {uploading ? (
          <div className="space-y-3">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
            <p className="text-sm text-gray-600">Caricamento in corso...</p>
          </div>
        ) : (
          <div className="space-y-3">
            <Upload className={`w-8 h-8 mx-auto ${currentImage ? 'text-gray-300' : 'text-gray-400'}`} />
            <div>
              <p className={`text-sm font-medium ${currentImage ? 'text-gray-500' : 'text-gray-900'}`}>
                {currentImage ? 'Carica una nuova immagine' : 'Trascina il file qui o'}{' '}
                <label className={`cursor-pointer underline ${currentImage ? 'text-gray-400 hover:text-gray-500' : 'text-purple-600 hover:text-purple-500'}`}>
                  {currentImage ? 'sostituisci' : 'seleziona file'}
                  <input
                    type="file"
                    className="hidden"
                    accept="image/jpeg,image/jpg,image/png,image/webp,application/pdf"
                    onChange={handleFileSelect}
                    disabled={uploading}
                  />
                </label>
              </p>
              <p className={`text-xs mt-1 ${currentImage ? 'text-gray-400' : 'text-gray-500'}`}>
                JPG, PNG, WebP o PDF • Massimo 10MB
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Upload Guidelines */}
      <div className="bg-blue-50 p-3 rounded-lg">
        <div className="flex">
          <AlertCircle className="w-4 h-4 text-blue-500 mt-0.5 mr-2 flex-shrink-0" />
          <div className="text-xs text-blue-700">
            <p className="font-medium mb-1">Linee guida per le immagini:</p>
            <ul className="space-y-1">
              <li>• Usa immagini di alta qualità (minimo 800x600px)</li>
              <li>• I file PDF sono supportati per cataloghi/schede tecniche</li>
              <li>• Evita immagini sfocate o con watermark</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FileUpload;